/** Announcements admin: form <-> payload and checks (pure, no React). */

export const STATUS = {
  ACTIVE: { label: "Active", tone: "green" },
  SCHEDULED: { label: "Scheduled", tone: "blue" },
  EXPIRED: { label: "Expired", tone: "gray" },
  INACTIVE: { label: "Inactive", tone: "gray" }, // turned off; not on dashboards
};
export const TITLE_MAX = 150;
export const BODY_MAX = 5000;
// companyId "" in the form = the user's own company; ALL = every company (super admin)
export const ALL_COMPANIES = "__all__";

const pad = (n) => String(n).padStart(2, "0");

/** ISO → "YYYY-MM-DDTHH:mm" for <input type="datetime-local"> (browser time). */
export const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** datetime-local value → ISO, or null when empty. */
export const fromLocalInput = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export const emptyForm = () => ({
  title: "",
  body: "",
  important: false,
  pinned: false,
  publishAt: "",
  expiresAt: "",
  companyId: "",
  branchIds: [],
});

/** Server row → form. */
export const fromRow = (row) => ({
  title: row.title || "",
  body: row.body || "",
  important: row.priority === "IMPORTANT",
  pinned: !!row.pinned,
  publishAt: toLocalInput(row.publishAt),
  expiresAt: toLocalInput(row.expiresAt),
  companyId: row.companyId === null ? ALL_COMPANIES : row.companyId || "",
  branchIds: row.branchIds || [],
});

/** Browser-side checks (the server validates too). */
export const formProblems = (f) => {
  const p = [];
  if (!f.title.trim()) p.push("Add a title.");
  if (f.title.length > TITLE_MAX)
    p.push(`The title is too long (max ${TITLE_MAX} characters).`);
  if (!f.body.trim()) p.push("Write the announcement.");
  if (f.body.length > BODY_MAX)
    p.push(`The text is too long (max ${BODY_MAX} characters).`);
  const pub = fromLocalInput(f.publishAt);
  const exp = fromLocalInput(f.expiresAt);
  if (exp && new Date(exp) <= new Date(pub || Date.now()))
    p.push("The end date must be after the publish date.");
  return p;
};

/** Form → POST / PATCH body. Blank publish = now (server default on create). */
export const toPayload = (f, { isEdit } = {}) => {
  const body = {
    title: f.title.trim(),
    body: f.body.trim(),
    priority: f.important ? "IMPORTANT" : "NORMAL",
    pinned: !!f.pinned,
    expiresAt: fromLocalInput(f.expiresAt),
    branchIds: f.branchIds,
  };
  const pub = fromLocalInput(f.publishAt);
  if (pub || isEdit) body.publishAt = pub || new Date().toISOString();
  if (f.companyId === ALL_COMPANIES) body.companyId = null;
  else if (f.companyId) body.companyId = f.companyId;
  return body;
};

/** Who sees it, in words. */
export const audienceText = (row, branchName = (id) => id) => {
  if (row.branchIds?.length)
    return row.branchIds.length <= 2
      ? row.branchIds.map(branchName).join(", ")
      : `${row.branchIds.length} branches`;
  if (row.companyId === null) return "Every company";
  return row.companyName || "Company-wide";
};
